import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { Avatar, ListItem, Paper, Typography } from '@material-ui/core';
import { alpha, makeStyles, useTheme } from '@material-ui/core/styles';
import AlternateEmailIcon from '@material-ui/icons/AlternateEmail';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import PersonIcon from '@material-ui/icons/Person';

import { isMencaoTodos } from 'utils/mencaoTodos';

import { MentionItem } from './mentionSuggestion';

export type MentionListHandle = {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
};

type MentionListProps = {
  items: MentionItem[];
  command: (attrs: {
    id: string;
    label: string;
    contact: string;
    waPhoneNumberId: string;
  }) => void;
  loading?: boolean;
};

const AVATAR_HUES = [210, 140, 30, 280, 350, 180];

const getHue = (text: string) => {
  const hash = Array.from(text).reduce(
    (acc, char) => acc + char.charCodeAt(0),
    0,
  );
  return AVATAR_HUES[hash % AVATAR_HUES.length];
};

const getIniciais = (nome: string) => {
  const palavras = nome
    .split(/\s+/)
    .map(p => p.replace(/[^\p{L}]/gu, ''))
    .filter(Boolean);
  if (palavras.length === 0) return '';
  const primeira = palavras[0].charAt(0);
  const ultima = palavras.length > 1 ? palavras[palavras.length - 1][0] : '';
  return `${primeira}${ultima}`.toUpperCase();
};

/** +5584998179177 → +55 84 99817-9177 (outros formatos ficam como vieram) */
const formatarTelefone = (contato: string) => {
  const digitos = contato.replace(/\D/g, '');
  const match = digitos.match(/^55(\d{2})(\d{4,5})(\d{4})$/);
  if (!match) return contato;
  return `+55 ${match[1]} ${match[2]}-${match[3]}`;
};

const useStyles = makeStyles(({ palette, spacing, zIndex, typography }) => ({
  paper: {
    width: 340,
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 320,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: spacing(1.5),
    border: `1px solid ${palette.divider}`,
    zIndex: zIndex.modal,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(0.5),
    padding: spacing(1.25, 2),
    borderBottom: `1px solid ${palette.divider}`,
    color: alpha(palette.text.primary, 0.75),
  },
  headerIcon: {
    fontSize: typography.pxToRem(16),
  },
  headerTitle: {
    fontSize: typography.pxToRem(13),
    fontWeight: 500,
  },
  content: {
    overflowY: 'auto',
    padding: spacing(0.75),
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1.5),
    padding: spacing(1, 1.5),
    borderRadius: spacing(1),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: palette.action.hover,
    },
  },
  selectedItem: {
    backgroundColor: palette.action.selected,
    '&:hover': {
      backgroundColor: palette.action.selected,
    },
  },
  selectedTodos: {
    backgroundColor: alpha(palette.primary.main, 0.12),
    '&:hover': {
      backgroundColor: alpha(palette.primary.main, 0.12),
    },
  },
  avatar: {
    width: spacing(4.5),
    height: spacing(4.5),
    fontSize: typography.pxToRem(13),
    fontWeight: 500,
  },
  avatarTodos: {
    backgroundColor: palette.primary.main,
    color: palette.primary.contrastText,
  },
  itemText: {
    minWidth: 0,
  },
  itemTitle: {
    fontSize: typography.pxToRem(13),
    fontWeight: 400,
  },
  itemTitleTodos: {
    fontSize: typography.pxToRem(13),
    fontWeight: 500,
  },
  itemSubtitle: {
    fontSize: typography.pxToRem(11.5),
  },
  emptyText: {
    padding: spacing(2),
    textAlign: 'center',
    color: palette.text.secondary,
    display: 'block',
  },
}));

export const MentionList = forwardRef<MentionListHandle, MentionListProps>(
  (props, ref) => {
    const { items, command, loading } = props;
    const classes = useStyles();
    const { palette } = useTheme();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [displayedItems, setDisplayedItems] = useState<MentionItem[]>(items);
    const itemRefs = useRef<Array<HTMLDivElement | null>>([]);

    useEffect(() => {
      if (!loading) {
        setDisplayedItems(items);
      }
    }, [items, loading]);

    useEffect(() => {
      setSelectedIndex(0);
    }, [displayedItems]);

    useEffect(() => {
      itemRefs.current[selectedIndex]?.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex]);

    const selectItem = (index: number) => {
      const item = displayedItems[index];
      if (item) {
        command({
          id: item.id,
          label: item.label,
          contact: item.contact,
          waPhoneNumberId: item.waPhoneNumberId,
        });
      }
    };

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (displayedItems.length === 0) {
          return false;
        }
        if (event.key === 'ArrowUp') {
          setSelectedIndex(
            i => (i + displayedItems.length - 1) % displayedItems.length,
          );
          return true;
        }
        if (event.key === 'ArrowDown') {
          setSelectedIndex(i => (i + 1) % displayedItems.length);
          return true;
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          selectItem(selectedIndex);
          return true;
        }
        return false;
      },
    }));

    const renderEmpty = () => {
      if (loading) {
        return (
          <Typography variant="caption" className={classes.emptyText}>
            Carregando participantes...
          </Typography>
        );
      }
      return (
        <Typography variant="caption" className={classes.emptyText}>
          Nenhum participante encontrado
        </Typography>
      );
    };

    const renderAvatar = (item: MentionItem) => {
      if (isMencaoTodos(item.waPhoneNumberId)) {
        return (
          <Avatar className={`${classes.avatar} ${classes.avatarTodos}`}>
            <PeopleOutlineIcon fontSize="small" />
          </Avatar>
        );
      }

      const iniciais = getIniciais(item.label ?? '');
      const hue = getHue(item.label || item.id);
      return (
        <Avatar
          src={item.foto}
          className={classes.avatar}
          style={
            palette.type === 'dark'
              ? {
                  backgroundColor: `hsla(${hue}, 50%, 50%, 0.25)`,
                  color: `hsl(${hue}, 70%, 80%)`,
                }
              : {
                  backgroundColor: `hsl(${hue}, 70%, 90%)`,
                  color: `hsl(${hue}, 55%, 30%)`,
                }
          }
        >
          {iniciais || <PersonIcon fontSize="small" />}
        </Avatar>
      );
    };

    const renderItem = (item: MentionItem, index: number) => {
      const todos = isMencaoTodos(item.waPhoneNumberId);
      const selectedClass = todos
        ? classes.selectedTodos
        : classes.selectedItem;

      return (
        <ListItem
          key={item.id}
          component="div"
          ref={(el: HTMLDivElement | null) => {
            itemRefs.current[index] = el;
          }}
          role="option"
          aria-selected={index === selectedIndex}
          className={`${classes.item} ${
            index === selectedIndex ? selectedClass : ''
          }`}
          onMouseDown={event => event.preventDefault()}
          onClick={() => selectItem(index)}
        >
          {renderAvatar(item)}
          <div className={classes.itemText}>
            <Typography
              variant="body2"
              noWrap
              className={todos ? classes.itemTitleTodos : classes.itemTitle}
            >
              {todos ? `@${item.label}` : item.label}
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              noWrap
              component="div"
              className={classes.itemSubtitle}
            >
              {todos ? item.contact : formatarTelefone(item.contact ?? '')}
            </Typography>
          </div>
        </ListItem>
      );
    };

    // @todos sempre no topo; os índices seguem displayedItems
    const todosIndex = displayedItems.findIndex(item =>
      isMencaoTodos(item.waPhoneNumberId),
    );
    const contatos = displayedItems
      .map((item, index) => ({ item, index }))
      .filter(({ index }) => index !== todosIndex);

    return (
      <Paper className={classes.paper} elevation={8}>
        <div className={classes.header}>
          <AlternateEmailIcon className={classes.headerIcon} />
          <Typography variant="body2" className={classes.headerTitle}>
            Mencionar
          </Typography>
        </div>
        <div className={classes.content} role="listbox">
          {todosIndex > -1 &&
            renderItem(displayedItems[todosIndex], todosIndex)}
          {contatos.length === 0
            ? renderEmpty()
            : contatos.map(({ item, index }) => renderItem(item, index))}
        </div>
      </Paper>
    );
  },
);

MentionList.displayName = 'MentionList';
